import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["month", "bulk", "selectionLabel", "zipName", "feedback", "history"]
  static values = { createUrl: String, countries: Array }
  connect() {
    this.timers = new Set()
    this.element.querySelectorAll("[data-status-url]").forEach(row => {
      if (["queued", "processing", "waiting"].includes(row.dataset.status)) this.poll(row.dataset.statusUrl, row)
    })
    try {
      const state = JSON.parse(sessionStorage.getItem("admin-accounting-selection") || "null")
      if (state?.year === this.monthTargets[0]?.value.slice(0, 4)) this.monthTargets.forEach(input => { input.checked = state.months.includes(input.value) })
    } catch (_) { /* Disabled storage must not stop downloads. */ }
    this.selection()
  }
  disconnect() { this.timers.forEach(timer => clearTimeout(timer)) }
  filter(event) { event.target.form.requestSubmit() }
  toggle(event) {
    const button = event.currentTarget, expanded = button.getAttribute("aria-expanded") === "true"
    button.setAttribute("aria-expanded", String(!expanded))
    document.getElementById(button.getAttribute("aria-controls")).hidden = expanded
  }
  selected() { return this.monthTargets.filter(input => input.checked).map(input => input.value).sort() }
  selection() {
    const months = this.selected()
    this.bulkTarget.hidden = months.length < 2
    this.selectionLabelTarget.textContent = `${months.length} months · ${months.length * (this.countriesValue.length * 5 + 2)} reports + external files`
    this.zipNameTarget.textContent = months.length ? `echoage-accounting-${months[0]}${months.length > 1 ? `_to_${months.at(-1)}` : ""}-${this.countriesValue.join("-")}.zip` : ""
    try { sessionStorage.setItem("admin-accounting-selection", JSON.stringify({ year: this.monthTargets[0]?.value.slice(0, 4), months })) } catch (_) {}
  }
  quick(event) {
    const quarter = event.currentTarget.dataset.quarter
    this.monthTargets.forEach(input => { input.checked = quarter === "all" || Math.ceil(Number(input.value.slice(-2)) / 3) === Number(quarter) })
    this.selection()
  }
  clear() { this.monthTargets.forEach(input => { input.checked = false }); this.selection() }
  message(text, error = false) {
    this.feedbackTarget.hidden = false
    this.feedbackTarget.textContent = text
    this.feedbackTarget.classList.toggle("is-error", error)
  }
  async request(url, options = {}) {
    const response = await fetch(url, { credentials: "same-origin", ...options,
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", ...options.headers } })
    if (response.redirected) throw new Error("Your admin session has expired. Reload this page and sign in again.")
    if (!response.headers.get("content-type")?.includes("application/json")) throw new Error("The report service could not complete this request. Reload and try again.")
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || "The request could not be completed. Please try again.")
    return result
  }
  async download(event) {
    const button = event.currentTarget, months = button.dataset.month ? [button.dataset.month] : this.selected()
    button.disabled = true
    try {
      const result = await this.request(this.createUrlValue, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ months, countries: button.dataset.country ? [button.dataset.country] : this.countriesValue, kind: button.dataset.kind }) })
      this.message("Preparing your download. You can continue using admin; it will appear in Recent downloads.")
      this.historyTarget.querySelector(".ac-empty")?.remove()
      const row = document.createElement("div")
      row.className = "ac-export"; row.dataset.exportId = result.id; row.dataset.title = months.join(", ")
      this.historyTarget.prepend(row); this.renderRun(result, row); this.poll(result.status_url, row)
    } catch (error) { this.message(error.message, true) }
    finally { button.disabled = false }
  }
  async poll(url, row) {
    if (!this.element.isConnected) return
    try {
      const result = await this.request(url)
      this.renderRun(result, row)
      if (!["queued", "processing", "waiting"].includes(result.status)) {
        this.message(result.status === "failed" ? (result.notes.join(" ") || "Preparation failed. Please retry.") : result.status === "partial" ? "Your ZIP is ready with missing files. Review Notes and the ZIP’s README before sharing it." : "Your report is ready in Recent downloads.", result.status !== "ready")
        return
      }
    } catch (error) { this.message(error.message, true); return }
    const timer = setTimeout(() => { this.timers.delete(timer); this.poll(url, row) }, 5000)
    this.timers.add(timer)
  }
  renderRun(result, row) {
    const title = result.filename || row.dataset.title || row.querySelector("span")?.textContent || "Preparing reports"
    row.replaceChildren()
    const name = document.createElement("span"); name.textContent = title; row.append(name)
    const status = document.createElement("span"); status.className = "ac-export-status"
    status.textContent = { queued: "Queued", processing: "Preparing…", waiting: "Preparing…", ready: "Ready", partial: "Ready · missing files", failed: "Needs attention" }[result.status]; row.append(status)
    if (result.download_url) { const link = document.createElement("a"); link.href = result.download_url; link.className = "ac-button"; link.textContent = "Download"; link.dataset.turbo = "false"; row.append(link) }
    if (result.notes?.length) {
      const details = document.createElement("details"), summary = document.createElement("summary"), list = document.createElement("ul")
      summary.textContent = "Notes"; details.append(summary)
      result.notes.forEach(note => { const item = document.createElement("li"); item.textContent = note; list.append(item) })
      details.append(list); row.append(details)
    }
  }
  async upload(event) {
    event.preventDefault()
    const form = event.currentTarget, button = form.querySelector("button"); button.disabled = true
    try {
      const result = await this.request(form.action, { method: "POST", body: new FormData(form) })
      this.message(result.message); window.location.reload()
    } catch (error) { this.message(error.message, true); button.disabled = false }
  }
  async removeUpload(event) {
    const button = event.currentTarget; button.disabled = true
    try { await this.request(button.dataset.url, { method: "DELETE" }); window.location.reload() }
    catch (error) { this.message(error.message, true); button.disabled = false }
  }
}
